import { Box, ButtonBase, CircularProgress, Stack, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import { apiGetFile } from '../../generic/Api_Functions'
// From the import-free leaf module — the previous imports (SmartDocumentViewer / the 2D
// viewer) dragged the heavy viewer implementations into every thumbnail consumer.
import { checkDocumentExtension, SmartBrowserDocumentViewerImageExtensions } from './documentExtensions'
import SensitiveBoundary from '../../vision/SensitiveBoundary'

// Historia 148: `contextQuery` es ADITIVO y opcional — los 4 query params del contexto MOS que el
// gate de Sun necesita en N2. Vacio (el caso de MIC oficina) la URL es exactamente la de antes.
const ThumbnailPreview = ({ document, tempUUID, contextQuery }) => {
  const [thumbUrl, setThumbUrl] = useState(null)
  const isImage = checkDocumentExtension(document?.extension, SmartBrowserDocumentViewerImageExtensions)

  const url = useMemo(() => {
    if (!document?.uuid) {
      return null
    }
    const basePath = tempUUID && !document.id
      ? 'document/file/download/' + document.uuid + '/' + tempUUID
      : 'document/file/download/' + document.uuid
    return basePath + contextQuery
  }, [document, tempUUID, contextQuery])

  useEffect(() => {
    if (!isImage || !url) {
      setThumbUrl(null)
      return
    }
    let isCurrent = true
    let currentUrl = null
    const loadThumb = async () => {
      try {
        const response = await apiGetFile(url)
        const blob = await response.blob()
        if (!isCurrent) {
          return
        }
        currentUrl = URL.createObjectURL(blob)
        if (isCurrent) {
          setThumbUrl(currentUrl)
        }
      } catch (error) {
        if (isCurrent) {
          setThumbUrl(null)
        }
      }
    }
    loadThumb()
    return () => {
      isCurrent = false
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl)
      }
    }
  }, [isImage, url])

  return (
    <Box
      sx={{
        width: '100%',
        height: 70,
        backgroundColor: isImage && thumbUrl ? 'grey.100' : 'grey.700',
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}
    >
      {isImage
        ? (
            thumbUrl
              ? (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    backgroundImage: `url(${thumbUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                  role='img'
                  aria-label={document?.documentName}
                />
                )
              : (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
                )
          )
        : (
          <Stack alignItems='center' spacing={0.5}>
            <InsertDriveFileOutlinedIcon sx={{ color: 'common.white' }} />
            <Typography variant='caption' sx={{ color: 'common.white', fontWeight: 700 }}>
              {(document?.extension || '').toUpperCase()}
            </Typography>
          </Stack>
          )}
    </Box>
  )
}

// Historia 213 v2 (S189, S195): the preview (image or extension tile) is the content, so it lives in its own
// component under the boundary: covered, it is unmounted and the image is neither kept nor requested (reopening 3,
// C15.6: the small blurred placeholder takes its place). The thumbnail is a <div role=button>.
const DocumentThumbnail = ({ document, tempUUID, isSelected, onSelect, titleAdornment = null, contextQuery = '' }) => {
  return (
    <ButtonBase
      component='div'
      onClick={onSelect}
      sx={{
        borderRadius: 1,
        border: '1px solid',
        borderColor: isSelected ? 'primary.main' : 'divider',
        padding: '0.25rem',
        width: 110,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        height: '100%'
      }}
    >
      <SensitiveBoundary sensitive={document?.sensitive === true} compact>
        <ThumbnailPreview document={document} tempUUID={tempUUID} contextQuery={contextQuery} />
      </SensitiveBoundary>
      <Stack direction='row' alignItems='center' gap={0.5} sx={{ width: '100%', minWidth: 0 }}>
        {titleAdornment}
        <Typography
          variant='caption'
          sx={{
            flex: 1,
            minWidth: 0,
            textAlign: titleAdornment ? 'left' : 'center',
            color: 'text.primary',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            wordBreak: 'break-word',
            lineHeight: 1.2,
            minHeight: '2.4em'
          }}
        >
          {document?.documentName || document?.fileName}
        </Typography>
      </Stack>
    </ButtonBase>
  )
}

export default DocumentThumbnail
